import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import { useEmailServerTest, type EmailServerViewModel } from '../api';

/** Sends a test email through a chosen server and shows the SES outcome inline. */
export function TestSendDialog({
  server,
  onClose,
}: {
  readonly server: EmailServerViewModel | null;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [to, setTo] = useState('');
  const test = useEmailServerTest();

  useEffect(() => {
    if (server !== null) {
      setTo('');
      test.reset();
    }
    // Only re-run when the target server changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.id]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (server === null || to.trim().length === 0) return;
    test.mutate({ id: server.id, to: to.trim() });
  }

  const result = test.data;

  return (
    <Dialog
      open={server !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.email.servers.test.title', { name: server?.name ?? '' })}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField id="email-test-to" label={t('routes.admin.email.servers.test.toLabel')} required>
            {(fp) => (
              <Input
                {...fp}
                type="email"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                }}
                placeholder={t('routes.admin.email.servers.test.toPlaceholder')}
                autoComplete="off"
              />
            )}
          </FormField>

          {test.isSuccess && result !== undefined && result.ok && (
            <p role="status" className="text-sm text-emerald-600">
              {t('routes.admin.email.servers.test.success', { id: result.message_id ?? '' })}
            </p>
          )}
          {test.isSuccess && result !== undefined && !result.ok && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.email.servers.test.failed', {
                code: result.error_code ?? 'unknown',
                detail: result.detail ?? '',
              })}
            </p>
          )}
          {test.isError && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.email.servers.test.error')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={test.isPending}>
              {t('routes.admin.email.servers.test.cancel')}
            </Button>
            <Button type="submit" loading={test.isPending} disabled={to.trim().length === 0}>
              {t('routes.admin.email.servers.test.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
